import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import Toggle from '@amzn/meridian/toggle';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Divider from '@amzn/meridian/divider';
import {
  adminApi,
  AccountRecord,
  DepartmentRecord,
  DiffResponse,
  PolicyRecord,
  TemplateRecord,
} from '../service/adminApiService';
import { OperationCheckboxGroup } from '../components/OperationCheckboxGroup';
import { AccountMultiSelect } from '../components/AccountMultiSelect';
import { DepartmentDropdown } from '../components/DepartmentDropdown';
import { PermissionDiffPanel } from '../components/PermissionDiffPanel';
import { useToast } from '../components/ToastProvider';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';

/**
 * Group create / edit / clone form.
 *
 * Route shapes this component handles:
 *   /admin/groups/new                         → create mode
 *   /admin/groups/new?clone_from=name         → clone mode (prefills from source)
 *   /admin/groups/:posix_group/edit           → edit mode
 */
export const GroupFormPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { posix_group: paramPosixGroup } = useParams<{ posix_group?: string }>();
  const [searchParams] = useSearchParams();
  const cloneFrom = searchParams.get('clone_from');

  const isEdit = Boolean(paramPosixGroup);
  const [mode] = useState<'create' | 'edit' | 'clone'>(
    isEdit ? 'edit' : cloneFrom ? 'clone' : 'create',
  );

  // Form state
  const [posixGroup, setPosixGroup] = useState('');
  const [department, setDepartment] = useState('GFT');
  const [description, setDescription] = useState('');
  const [operations, setOperations] = useState<string[]>([]);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [prodAccess, setProdAccess] = useState(false);
  const [confirmLockout, setConfirmLockout] = useState(false);

  // Reference data
  const [accounts, setAccounts] = useState<AccountRecord[]>([]);
  const [templates, setTemplates] = useState<TemplateRecord[]>([]);
  const [departments, setDepartments] = useState<DepartmentRecord[]>([]);
  const [templateName, setTemplateName] = useState('');

  // Preview + save state
  const [diff, setDiff] = useState<DiffResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = useMemo(() => {
    if (mode === 'edit') return `Edit group: ${paramPosixGroup}`;
    if (mode === 'clone') return `Clone group: ${cloneFrom}`;
    return 'New group';
  }, [mode, paramPosixGroup, cloneFrom]);

  // Load reference data + existing record (for edit/clone).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [accountsResp, templatesResp, departmentsResp] = await Promise.all([
          adminApi.listAccounts(),
          adminApi.listTemplates(),
          adminApi.listDepartments(),
        ]);
        if (cancelled) return;
        setAccounts(accountsResp.accounts);
        setTemplates(templatesResp.templates);
        setDepartments(departmentsResp.departments);

        if (mode === 'edit' && paramPosixGroup) {
          const existing = await adminApi.getGroup(paramPosixGroup);
          if (cancelled) return;
          setPosixGroup(existing.posix_group);
          setDepartment(existing.department || 'GFT');
          setDescription(existing.description || '');
          setOperations(existing.allowed_operations);
          setAccountIds(existing.allowed_account_ids);
          setProdAccess(existing.prod_access);
        } else if (mode === 'clone' && cloneFrom) {
          const source = await adminApi.getGroup(cloneFrom);
          if (cancelled) return;
          setPosixGroup(''); // user must supply a fresh name
          setDepartment(source.department || 'GFT');
          setDescription(`Cloned from ${source.posix_group}. ${source.description}`.trim());
          setOperations(source.allowed_operations);
          setAccountIds(source.allowed_account_ids);
          setProdAccess(source.prod_access);
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load form data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, paramPosixGroup, cloneFrom]);

  const applyTemplate = useCallback(
    (name: string) => {
      setTemplateName(name);
      if (!name) return;
      const template = templates.find((t) => t.template_name === name);
      if (!template) return;
      setOperations(template.operations);
      toast.push({
        type: 'neutral',
        message: `Applied template ${name}: ${template.operations.length} operation(s)`,
      });
    },
    [templates, toast],
  );

  const payload = useMemo(
    () => ({
      posix_group: posixGroup,
      department,
      description,
      allowed_operations: operations,
      allowed_account_ids: accountIds,
      prod_access: prodAccess,
    }),
    [posixGroup, department, description, operations, accountIds, prodAccess],
  );

  // Invalidate the preview diff whenever any form field that feeds into
  // ``payload`` changes. Without this, an admin could preview, then edit
  // any value, and still hit Save — the save path posts the *current*
  // payload, not the previewed one, which would silently defeat the
  // safety gate.
  useEffect(() => {
    setDiff(null);
  }, [payload]);

  const runPreview = async () => {
    if (!posixGroup) {
      toast.push({ type: 'error', message: 'POSIX group name is required before previewing' });
      return;
    }
    setPreviewing(true);
    try {
      const response = await adminApi.previewGroup(posixGroup, payload);
      setDiff(response);
    } catch (err: any) {
      toast.push({ type: 'error', message: err?.message || 'Preview failed' });
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (mode === 'edit' && paramPosixGroup) {
        await adminApi.updateGroup(paramPosixGroup, {
          ...payload,
          confirm_lockout_risk: confirmLockout,
        });
      } else {
        await adminApi.createGroup({
          ...payload,
          ...(mode === 'clone' && cloneFrom ? { cloned_from: cloneFrom } : {}),
        });
      }
      toast.push({
        type: 'success',
        message: mode === 'edit' ? `Updated ${posixGroup}` : `Created ${posixGroup}`,
      });
      navigate('/admin/groups');
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const canSave = diff !== null && posixGroup && operations.length > 0 && accountIds.length > 0;

  if (loading) {
    return (
      <Row alignmentHorizontal="center" spacingInset="500">
        <Loader type="circular" size="large" />
      </Row>
    );
  }

  return (
    <Column spacing="large">
      <PageHeader title={title} />

      {error && <Alert type="error">{error}</Alert>}

      <Section>
      <Column spacing="medium" maxWidth={900}>
        <Text type="h100">Identity</Text>
        <Row widths={['fill', 'fill']} spacing="medium" alignmentVertical="top" wrap="down">
          <Column spacing="xsmall">
            <label htmlFor="posix-group">POSIX group name</label>
            <Input
              id="posix-group"
              type="text"
              value={posixGroup}
              onChange={setPosixGroup}
              placeholder="e.g. twitch-ops"
              disabled={mode === 'edit'}
              aria-describedby="posix-group-hint"
            />
            <Text id="posix-group-hint" type="b200" color="secondary">
              Lowercase letters, digits, dot, underscore, or hyphen. 3–64 characters.
            </Text>
          </Column>
          <Column spacing="xsmall">
            <label htmlFor="department">Department</label>
            <DepartmentDropdown
              value={department}
              onChange={setDepartment}
              departments={departments}
              onQuickAdd={async (name) => {
                try {
                  await adminApi.createDepartment({ department_name: name, display_name: name });
                  const departmentsResp = await adminApi.listDepartments();
                  setDepartments(departmentsResp.departments);
                  toast.push({ type: 'success', message: `Created department ${name}` });
                } catch (err: any) {
                  toast.push({ type: 'error', message: err?.message || 'Create department failed' });
                  // Re-throw so ``DepartmentDropdown`` can skip its
                  // success path and avoid setting the form's department
                  // value to a name the backend didn't actually create.
                  throw err;
                }
              }}
            />
          </Column>
        </Row>

        <Column spacing="xsmall">
          <label htmlFor="description">Description</label>
          <Input
            id="description"
            type="text"
            value={description}
            onChange={setDescription}
            placeholder="Short description shown in the admin UI"
          />
        </Column>

        <Divider />

        <Text type="h100">Allowed operations</Text>
        <OperationCheckboxGroup
          value={operations}
          onChange={setOperations}
          templates={templates.map((t) => ({
            name: t.template_name,
            operations: t.operations,
            description: t.description,
            is_system: t.is_system,
          }))}
          onSelectTemplate={applyTemplate}
        />

        <Divider />

        <Text type="h100">Allowed accounts</Text>
        <AccountMultiSelect accounts={accounts} value={accountIds} onChange={setAccountIds} />

        <Divider />

        <Row alignmentVertical="center" spacing="medium">
          <Toggle checked={prodAccess} onChange={setProdAccess}>
            Production access
          </Toggle>
          <Text type="b200" color="secondary">
            When enabled, members can operate on production-classified accounts.
          </Text>
        </Row>

        {mode === 'edit' && (
          <Row alignmentVertical="center" spacing="medium">
            <Toggle checked={confirmLockout} onChange={setConfirmLockout}>
              I understand the lockout risk
            </Toggle>
            <Text type="b200" color="secondary">
              Required when editing the ft-cogit-admin group to remove the admin operation or
              disable prod access.
            </Text>
          </Row>
        )}

        <Divider />

        <Row alignmentHorizontal="end" spacing="small">
          <Button type="tertiary" onClick={() => navigate('/admin/groups')}>
            Cancel
          </Button>
          <Button type="secondary" onClick={runPreview} disabled={previewing}>
            {previewing ? 'Previewing…' : 'Preview changes'}
          </Button>
          <Button type="primary" onClick={save} disabled={!canSave || saving}>
            {saving ? 'Saving…' : mode === 'edit' ? 'Confirm changes' : 'Create group'}
          </Button>
        </Row>

        {diff && <PermissionDiffPanel diff={diff} />}
      </Column>
      </Section>
    </Column>
  );
};
